import { formatReadableNumber } from '@lfg/core/lib';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Grid2';

import { colors } from 'src/global/styles/colors';
import { useAnalyticsContext } from 'src/hooks/useAnalyticsContext';
import { useGetOverviewQuery } from 'src/store/services/analytics.service';

interface KPICardProps {
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'positive' | 'negative';
  showViewDetails?: boolean;
  isLoading?: boolean;
}

function KPICard({ title, value, change, changeType, showViewDetails, isLoading }: KPICardProps) {
  return (
    <Box
      sx={{
        padding: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(100px)',
        borderRadius: '8px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}>
      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: 400,
          color: colors.neutral90,
          fontFamily: 'Gotham',
        }}>
        {title}
      </Typography>

      <Stack spacing={1} sx={{ flex: 1 }}>
        {isLoading ? (
          <Skeleton variant='text' width='60%' height={36} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
        ) : (
          <Typography
            sx={{
              fontSize: '26px',
              fontWeight: 700,
              color: colors.white,
              fontFamily: 'Gotham',
              lineHeight: 1.2,
            }}>
            {value}
          </Typography>
        )}

        {change && !isLoading && (
          <Stack direction='row' alignItems='center' spacing={0.75}>
            {changeType === 'positive' ? (
              <NorthEastIcon fontSize='small' sx={{ color: colors.green200, width: 12, height: 12 }} />
            ) : (
              <SouthEastIcon fontSize='small' sx={{ color: colors.red200, width: 12, height: 12 }} />
            )}
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: changeType === 'positive' ? colors.green200 : colors.red200,
                fontFamily: 'Gotham',
              }}>
              {change}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 400,
                color: colors.neutral90,
                fontFamily: 'Gotham',
              }}>
              vs last week
            </Typography>
          </Stack>
        )}

        {isLoading && change && (
          <Skeleton variant='text' width='40%' height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
        )}

        {showViewDetails && (
          <Button
            variant='text'
            endIcon={<ArrowForwardIosIcon />}
            sx={{
              justifyContent: 'flex-end',
              padding: 0,
              minWidth: 'auto',
              color: colors.white,
              fontSize: '12px',
              fontWeight: 400,
              fontFamily: 'Gotham',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '& .MuiButton-endIcon': {
                marginLeft: 1,
                '& svg': {
                  fontSize: '16px',
                },
              },
            }}>
            View Details
          </Button>
        )}
      </Stack>
    </Box>
  );
}

export function KPIOverviewCards() {
  const { address, network } = useAnalyticsContext();
  const {
    data: overviewData,
    isLoading,
    error,
  } = useGetOverviewQuery({ address, network }, { skip: !address || !network });

  // Helper function to format percentage change
  const formatPercentage = (value: number) => {
    const absValue = Math.abs(value);
    return `${absValue?.toFixed(1)}%`;
  };

  if (error) {
    console.error('Failed to load overview data:', error);
  }

  return (
    <Grid2 container spacing={3}>
      <Grid2 size={{ xs: 12, sm: 6, lg: 4 }}>
        <KPICard
          title='Total Projects'
          value={overviewData?.totalProjects ?? 0}
          showViewDetails
          isLoading={isLoading}
        />
      </Grid2>

      <Grid2 size={{ xs: 12, sm: 6, lg: 4 }}>
        <KPICard
          title='Total Core Sold'
          value={overviewData?.totalNodeSold?.toLocaleString() ?? '0'}
          change={overviewData ? formatPercentage(overviewData.percentNodeSoldLastWeek) : undefined}
          changeType={overviewData && overviewData.percentNodeSoldLastWeek >= 0 ? 'positive' : 'negative'}
          isLoading={isLoading}
        />
      </Grid2>

      <Grid2 size={{ xs: 12, sm: 6, lg: 4 }}>
        <KPICard
          title='Total Sales Revenue'
          value={
            overviewData ? formatReadableNumber(overviewData.totalRevenues || 0, { maximumFractionDigits: 2 }) : '$0.0'
          }
          change={overviewData ? formatPercentage(overviewData.percentRevenuesLastWeek) : undefined}
          changeType={overviewData && overviewData.percentRevenuesLastWeek >= 0 ? 'positive' : 'negative'}
          isLoading={isLoading}
        />
      </Grid2>
    </Grid2>
  );
}
